import { useCallback, useMemo, useState } from 'react'
import {
    CalendarDay,
    generateTimeSlots,
    getEffectiveHours,
    getEventsForDay,
    getEventIntervals,
    buildGridSegments,
    getGridHeight,
    createMinutesToPixels,
    layoutDayEvents,
    WEEKDAY_NAMES,
} from '../../utils/calendar-utils'
import { AgendaEvent } from '@/lib/api/common/interfaces/agenda.interface'
import { EventCard } from '../events/EventCard'
import { DayGrid, TimeRuler } from './TimeSlot'

interface WeekViewProps {
    days: CalendarDay[]
    events: AgendaEvent[]
    workingHours?: { start: number; end: number }
    slotDuration?: number
    onSlotClick?: (date: Date) => void
    onEventClick?: (event: AgendaEvent) => void
}

export function WeekView({
    days,
    events,
    workingHours = { start: 8, end: 18 },
    slotDuration = 30,
    onSlotClick,
    onEventClick,
}: WeekViewProps) {
    const [expandedGaps, setExpandedGaps] = useState<ReadonlySet<string>>(
        new Set()
    )

    const handleExpandGap = useCallback((key: string) => {
        setExpandedGaps(previous => new Set(previous).add(key))
    }, [])

    const handleCollapseGap = useCallback((key: string) => {
        setExpandedGaps(previous => {
            const next = new Set(previous)
            next.delete(key)
            return next
        })
    }, [])

    // A janela é única para a semana inteira: as colunas dos dias compartilham a
    // mesma régua de horários da esquerda.
    const weekEvents = useMemo(
        () => days.flatMap(day => getEventsForDay(events, day.date)),
        [days, events]
    )

    const hours = useMemo(
        () => getEffectiveHours(weekEvents, workingHours),
        [weekEvents, workingHours]
    )

    const timeSlots = useMemo(
        () => generateTimeSlots(new Date(), hours.start, hours.end, slotDuration),
        [hours, slotDuration]
    )

    // Um horário só é recolhido quando está vazio em TODOS os dias da semana —
    // as colunas dividem a mesma régua e precisam da mesma altura.
    const segments = useMemo(
        () =>
            buildGridSegments(
                timeSlots,
                getEventIntervals(weekEvents, hours, slotDuration),
                slotDuration,
                expandedGaps
            ),
        [timeSlots, weekEvents, hours, slotDuration, expandedGaps]
    )

    const minutesToPixels = useMemo(
        () => createMinutesToPixels(segments, slotDuration),
        [segments, slotDuration]
    )

    const eventsByDay = useMemo(
        () =>
            days.map(day =>
                layoutDayEvents(
                    getEventsForDay(events, day.date),
                    hours,
                    slotDuration,
                    minutesToPixels
                )
            ),
        [days, events, hours, slotDuration, minutesToPixels]
    )

    const gridHeight = getGridHeight(segments)

    const handleSlotClick = (day: CalendarDay, time: Date) => {
        if (day.isPast) return
        if (onSlotClick) {
            const slotDate = new Date(day.date)
            slotDate.setHours(time.getHours(), time.getMinutes(), 0, 0)
            onSlotClick(slotDate)
        }
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="flex border-b border-base-300">
                <div className="w-16 flex-shrink-0 border-r border-base-300"></div>
                {days.map((day, index) => (
                    <div
                        key={index}
                        className={`flex-1 px-2 py-3 text-center border-r border-base-300 last:border-r-0 ${
                            day.isToday ? 'bg-secondary/10' : ''
                        }`}
                    >
                        <div className="text-xs font-medium text-base-content/60">
                            {WEEKDAY_NAMES[index]}
                        </div>
                        <div
                            className={`text-lg font-semibold ${
                                day.isToday
                                    ? 'w-8 h-8 mx-auto flex items-center justify-center rounded-full bg-secondary text-secondary-content'
                                    : 'text-base-content'
                            }`}
                        >
                            {day.dayNumber}
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex-1 overflow-y-auto calendar-scroll">
                <div className="flex">
                    <TimeRuler segments={segments} />
                    <div className="flex-1 flex">
                        {days.map((day, dayIndex) => (
                            <div
                                key={dayIndex}
                                className={`flex-1 min-w-0 border-r border-base-300 last:border-r-0 relative ${
                                    day.isToday ? 'bg-secondary/5' : ''
                                }`}
                                style={{ height: `${gridHeight}px` }}
                            >
                                <DayGrid
                                    segments={segments}
                                    isPast={day.isPast}
                                    showGapLabel={false}
                                    onSlotClick={time =>
                                        handleSlotClick(day, time)
                                    }
                                    onExpandGap={handleExpandGap}
                                    onCollapseGap={handleCollapseGap}
                                />

                                <div className="absolute inset-0 pointer-events-none">
                                    {eventsByDay[dayIndex].map(
                                        ({
                                            event,
                                            top,
                                            height,
                                            left,
                                            width,
                                            columnCount,
                                        }) => (
                                            <div
                                                key={event.id}
                                                className="absolute px-0.5 pointer-events-auto"
                                                style={{
                                                    top: `${top}px`,
                                                    height: `${height}px`,
                                                    left: `${left}%`,
                                                    width: `${width}%`,
                                                }}
                                            >
                                                <div className="h-full overflow-hidden">
                                                    <EventCard
                                                        event={event}
                                                        onClick={onEventClick}
                                                        compact={
                                                            height < 44 ||
                                                            columnCount > 2
                                                        }
                                                        showTime={height >= 104}
                                                        showDetails={
                                                            height >= 128
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
